/* Helpers for importing/exporting per-event notes (annotations).

   Decode: annotations arrive as WebVTT NOTE lines attached to a cue block, or as
   XML comments trailing a cue element (eg. <p>...</p><!--annotation-->) in
   TTML/XML based formats (EZTitles, OOONA style).

   Encode: when the "Encode Notes" format option is enabled, event.notes is
   written back out using the same conventions. */

const VTT_NOTE_REGEX = /^NOTE($|\s)/;

/* Matches a cue-level element (<p> or <Subtitle>, optionally namespaced),
   optionally followed by an XML comment containing the cue's annotation. */
const CUE_WITH_COMMENT_REGEX = /<((?:[A-Za-z0-9]+:)?(?:p|Subtitle))\b([^>]*?)(\/>|>[\s\S]*?<\/\1>)(?:\s*<!--([\s\S]*?)-->)?/g;

/* Same as above without the comment part - used to locate injection points. */
const CUE_ELEMENT_REGEX = /<((?:[A-Za-z0-9]+:)?(?:p|Subtitle))\b([^>]*?)(\/>|>[\s\S]*?<\/\1>)/g;
const BEGIN_ATTR_REGEX = /(?:begin|TimeIn)\s*=\s*["']([^"']*)["']/;
function getBeginValue(attrs) {
  let match = (attrs || "").match(BEGIN_ATTR_REGEX);
  return match ? match[1] : undefined;
}

/* XML comments cannot contain "--" and cannot end with "-" */
function sanitizeXmlCommentText(notes) {
  let text = String(notes).trim();
  text = text.replace(/-{2,}/g, match => {
    return match.split("").join(" ");
  });
  if (text.endsWith("-")) {
    text += " ";
  }
  return text;
}

/* Group consecutive extracted entries that share the same begin timecode. Used
   for profiles that write one <p> per line of the same cue (eg. Netflix TT
   Captions). */
function groupByBegin(entries) {
  let groups = [];
  entries.forEach(entry => {
    let lastGroup = groups[groups.length - 1];
    if (lastGroup && entry.begin !== undefined && lastGroup.begin === entry.begin) {
      lastGroup.items.push(entry);
    } else {
      groups.push({
        begin: entry.begin,
        items: [entry]
      });
    }
  });
  return groups;
}
export default {
  /* True when the format option "Encode Notes" is enabled */
  shouldEncodeNotes: function (encodingOptions) {
    return !!(encodingOptions && encodingOptions["Encode Notes"] && encodingOptions["Encode Notes"] !== "false");
  },
  /* --- WebVTT --- */
  isVttNoteLine: function (line) {
    return VTT_NOTE_REGEX.test(line);
  },
  stripVttNotePrefix: function (line) {
    return line.replace(/^NOTE\s?/, "").trim();
  },
  appendNote: function (existingNotes, noteText) {
    if (!noteText) {
      return existingNotes || "";
    }
    return existingNotes ? existingNotes + "\n" + noteText : noteText;
  },
  /* Format event.notes as a VTT NOTE line block (without trailing newline) */
  formatVttNote: function (notes) {
    let text = String(notes).trim().replace(/-->/g, "->");
    let lines = text.split("\n").map(line => {
      return line.trim();
    }).filter(line => {
      return line !== "";
    });
    return "NOTE " + lines.join("\n");
  },
  /* --- XML based formats --- */

  /* Scan raw XML (before it gets minified/parsed) and return one entry per
     cue element in document order: [{ notes, begin }, ...] */
  extractXmlCueNotes: function (xml) {
    if (typeof xml !== "string") {
      return [];
    }
    let extracted = [];
    let match;
    CUE_WITH_COMMENT_REGEX.lastIndex = 0;
    while ((match = CUE_WITH_COMMENT_REGEX.exec(xml)) !== null) {
      extracted.push({
        notes: match[4] !== undefined ? match[4].trim() : "",
        begin: getBeginValue(match[2])
      });
    }
    return extracted;
  },
  /* Attach extracted notes to decoded events. Handles both 1:1 (one cue
     element per event) and grouped (multiple elements per event sharing the
     same begin time) layouts. Skips silently when alignment is ambiguous. */
  attachXmlCueNotes: function (events, extracted) {
    if (!Array.isArray(events) || !Array.isArray(extracted)) {
      return events;
    }
    let hasNotes = extracted.some(entry => {
      return entry.notes;
    });
    if (!hasNotes) {
      return events;
    }
    if (extracted.length === events.length) {
      events.forEach((event, index) => {
        if (extracted[index].notes) {
          event.notes = this.appendNote(event.notes, extracted[index].notes);
        }
      });
    } else if (extracted.length > events.length) {
      let groups = groupByBegin(extracted);
      if (groups.length === events.length) {
        events.forEach((event, index) => {
          let groupNotes = groups[index].items.map(item => {
            return item.notes;
          }).filter(note => {
            return note;
          }).join("\n");
          if (groupNotes) {
            event.notes = this.appendNote(event.notes, groupNotes);
          }
        });
      }
    }
    return events;
  },
  /* Inject event.notes as XML comments after the cue element(s) of each event
     in the encoded output. Handles 1:1 and grouped (multiple cue elements per
     event) layouts; the comment is placed after the last element of an event. */
  injectXmlCueNotes: function (xml, events) {
    if (typeof xml !== "string" || !Array.isArray(events)) {
      return xml;
    }
    let hasNotes = events.some(event => {
      return event && event.notes && String(event.notes).trim() !== "";
    });
    if (!hasNotes) {
      return xml;
    }
    let elements = [];
    let match;
    CUE_ELEMENT_REGEX.lastIndex = 0;
    while ((match = CUE_ELEMENT_REGEX.exec(xml)) !== null) {
      elements.push({
        end: match.index + match[0].length,
        begin: getBeginValue(match[2])
      });
    }
    if (elements.length === 0) {
      return xml;
    }

    /* Map each event to the position where its comment should be injected */
    let insertions = [];
    if (elements.length === events.length) {
      events.forEach((event, index) => {
        if (event && event.notes && String(event.notes).trim() !== "") {
          insertions.push({
            position: elements[index].end,
            text: sanitizeXmlCommentText(event.notes)
          });
        }
      });
    } else if (elements.length > events.length) {
      let groups = groupByBegin(elements);
      if (groups.length === events.length) {
        events.forEach((event, index) => {
          if (event && event.notes && String(event.notes).trim() !== "") {
            let groupItems = groups[index].items;
            insertions.push({
              position: groupItems[groupItems.length - 1].end,
              text: sanitizeXmlCommentText(event.notes)
            });
          }
        });
      }
    }

    /* Apply from the end of the document so positions stay valid */
    insertions.sort((entryA, entryB) => {
      return entryB.position - entryA.position;
    });
    insertions.forEach(insertion => {
      xml = xml.slice(0, insertion.position) + `<!--${insertion.text}-->` + xml.slice(insertion.position);
    });
    return xml;
  }
};